import {
  FM_OPERATIONAL_REGION_BAND_TONES,
  type FmOperationalRegionDescriptor,
} from "./FmOperationalWorkspaceRoot.types";
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalOneOf as oneOf,
  operationalText as text,
  optionalOperationalText as optionalText,
} from "./FmOperationalWorkspaceValidation.shared";
import { validateSection } from "./sections/section.validation";
import { assertArchetypePaceCapabilities } from "./sections/paceCapabilities";

/**
 * A region's own non-ready surface. It NAMES the missing thing rather than
 * restating the region's title, so the "<title> is unavailable." template cannot
 * come back, and any control it offers has to be one the owner can press
 */
function validateUnavailable(
  value: unknown,
  path: string,
  controlIds: ReadonlySet<string>,
  disabledControlIds: ReadonlySet<string>,
): void {
  const unavailable = object(value, path);
  exact(
    unavailable,
    new Set(["title", "reason", "resolvingActionId", "reasonId"]),
    path,
  );
  const title = text(unavailable.title, `${path}.title`);
  text(unavailable.reason, `${path}.reason`);
  if (/\bis unavailable\.?$/i.test(title)) {
    fail(
      `${path}.title`,
      "must name what is missing, not restate the region title",
    );
  }
  if (unavailable.reasonId !== undefined) {
    const reasonId = text(unavailable.reasonId, `${path}.reasonId`);
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(reasonId)) {
      fail(
        `${path}.reasonId`,
        "must be a lowercase slug so the DOM attribute is machine-readable",
      );
    }
  }
  if (unavailable.resolvingActionId === undefined) return;
  const actionId = text(
    unavailable.resolvingActionId,
    `${path}.resolvingActionId`,
  );
  if (!controlIds.has(actionId))
    fail(`${path}.resolvingActionId`, "must reference a declared control");
  if (disabledControlIds.has(actionId)) {
    fail(`${path}.resolvingActionId`, "must not reference a disabled control");
  }
}

/**
 * A destination must be pressable. The commit stage rendered four inert
 * eyebrow-and-value pairs under a heading promising places to go
 * so every destination names a declared control, and
 * a DISABLED one is refused outright — a greyed destination is the same dead end
 * wearing a button.
 */
function validateHandoffRegion(
  region: FmOperationalRegionDescriptor & Record<string, unknown>,
  path: string,
  controlIds: ReadonlySet<string>,
  disabledControlIds: ReadonlySet<string>,
  checkControls: boolean,
): void {
  exact(
    region,
    new Set([
      "kind",
      "id",
      "eyebrow",
      "title",
      "lede",
      "unavailable",
      "destinations",
      "scope",
      "bandTone",
    ]),
    path,
  );
  const destinations = array(region.destinations, `${path}.destinations`);
  if (!destinations.length)
    fail(`${path}.destinations`, "must name at least one destination");
  const ids = new Set<string>();
  destinations.forEach((item, index) => {
    const itemPath = `${path}.destinations[${index}]`;
    const destination = object(item, itemPath);
    exact(
      destination,
      new Set([
        "id",
        "purpose",
        "label",
        "outcome",
        "ownerLabel",
        "availabilityLabel",
        "actionId",
      ]),
      itemPath,
    );
    ids.add(text(destination.id, `${itemPath}.id`));
    if (destination.purpose !== undefined) {
      oneOf(
        destination.purpose,
        [
          "implementation",
          "analysis",
          "measurement",
          "market",
          "overview",
          "assistant",
        ],
        `${itemPath}.purpose`,
      );
    }
    text(destination.label, `${itemPath}.label`);
    text(destination.outcome, `${itemPath}.outcome`);
    optionalText(destination.ownerLabel, `${itemPath}.ownerLabel`);
    optionalText(
      destination.availabilityLabel,
      `${itemPath}.availabilityLabel`,
    );
    const actionId = text(destination.actionId, `${itemPath}.actionId`);
    if (checkControls) {
      requireDestinationControl(actionId, itemPath, controlIds, disabledControlIds);
    }
  });
  if (ids.size !== destinations.length)
    fail(`${path}.destinations`, "must use unique ids");
}

function requireDestinationControl(
  actionId: string,
  itemPath: string,
  controlIds: ReadonlySet<string>,
  disabledControlIds: ReadonlySet<string>,
): void {
  if (!controlIds.has(actionId))
    fail(`${itemPath}.actionId`, "must reference a declared control");
  if (disabledControlIds.has(actionId)) {
    fail(`${itemPath}.actionId`, "must not reference a disabled control");
  }
}

/**
 * A handoff destination must press a declared, enabled control. Under the
 * `shell` scope the rendered region runs this inside its own error boundary,
 * so a destination naming an undeclared control fails that region only.
 */
export function validateHandoffControlReferences(
  region: FmOperationalRegionDescriptor,
  path: string,
  controls: readonly { id: string; disabled?: boolean }[],
): void {
  if (region.kind !== "handoff") return;
  const controlIds = new Set(controls.map((control) => control.id));
  const disabledControlIds = new Set(
    controls.filter((control) => control.disabled === true).map((control) => control.id),
  );
  region.destinations.forEach((destination, index) =>
    requireDestinationControl(
      destination.actionId,
      `${path}.destinations[${index}]`,
      controlIds,
      disabledControlIds,
    ),
  );
}

/**
 * `whole` checks each section region's section and each handoff destination's
 * control too; `shell` leaves both to the rendered region, which runs the same
 * checks inside its own error boundary, so a live workspace scopes a region's
 * contract failure to that region instead of throwing for the whole route.
 */
export type OperationalRegionValidationScope = "whole" | "shell";

function validateRegion(
  regionValue: unknown,
  index: number,
  controlIds: ReadonlySet<string>,
  disabledControlIds: ReadonlySet<string>,
  scope: OperationalRegionValidationScope,
): void {
  const path = `descriptor.regions[${index}]`;
  const region = object(regionValue, path) as FmOperationalRegionDescriptor &
    Record<string, unknown>;
  const kind = text(region.kind, `${path}.kind`);
  text(region.id, `${path}.id`);
  optionalText(region.eyebrow, `${path}.eyebrow`);
  text(region.title, `${path}.title`);
  /* An empty lede is not "no lede": it renders an empty paragraph and holds the
     story rail open over the void the field exists to close, so it is refused
     rather than coerced. */
  optionalText(region.lede, `${path}.lede`);
  oneOf(region.scope, ["selection", "workspace"], `${path}.scope`);
  if (region.bandTone !== undefined) {
    oneOf(region.bandTone, FM_OPERATIONAL_REGION_BAND_TONES, `${path}.bandTone`);
  }
  validateUnavailable(
    region.unavailable,
    `${path}.unavailable`,
    controlIds,
    disabledControlIds,
  );
  if (region.kind === "section") {
    exact(
      region,
      new Set(["kind", "id", "eyebrow", "title", "lede", "unavailable", "scope", "section", "bandTone"]),
      path,
    );
    if (scope === "whole") {
      validateSection(region.section, `${path}.section`);
      assertArchetypePaceCapabilities("FmOperationalWorkspaceRoot", region.section, `${path}.section`);
    }
    return;
  }
  if (region.kind === "handoff") {
    validateHandoffRegion(region, path, controlIds, disabledControlIds, scope === "whole");
    return;
  }
  fail(
    `${path}.kind`,
    `is not registered: "${kind}"; table, chart and map content are elements inside a section region`,
  );
}

export function validateOperationalRegions(
  value: unknown,
  controlIds: ReadonlySet<string>,
  disabledControlIds: ReadonlySet<string>,
  scope: OperationalRegionValidationScope,
): void {
  const regions = array(value, "descriptor.regions");
  regions.forEach((item, index) =>
    validateRegion(item, index, controlIds, disabledControlIds, scope),
  );
  const ids = regions.map((item, index) =>
    text(
      object(item, `descriptor.regions[${index}]`).id,
      `descriptor.regions[${index}].id`,
    ),
  );
  if (new Set(ids).size !== ids.length) {
    fail("descriptor.regions", "must use unique ids");
  }
}
